/**
 * News, act-now first: the handful past the gate, then everything else. D82.
 *
 * 🔴 **The page states its own accuracy contract, at the top, before any headline.** This screen
 * pairs a story with a percentage, and every one of those percentages depends on a price read at
 * some moment that is not now. A reader who cannot see how fresh the numbers are cannot weigh any
 * of them, so the strip is the first thing on the page and every figure in it comes from a query.
 *
 * ## 🔴 "Sent" and "meets the gate" are different questions, and the page asks both
 *
 * `alerted` records that a message went out under whichever thresholds were live at that moment.
 * Both were retuned on 2026-08-20, so measured over 48 hours to 2026-08-21: **77 sent**, **55
 * stories meet the gate as it stands**, and of the 77 sent only **26** meet today's 4.0σ. Neither
 * count contains the other — the company-day dedup collapses siblings, so a qualifying story may
 * never have been sent.
 *
 * ⛔ So the heading says *sent*, and the gate is stated beside it as the gate **now in force**
 * rather than as a description of the list. A heading reading "past the gate · 4.0σ" above 77 rows
 * would tell the reader all 77 cleared 4.0σ, and 51 did not.
 *
 * ## ⛔ Nothing here is advice, and the move is not attribution
 *
 * `pctSinceNews` says the price moved after the story landed. A story and a Fed leak in the same
 * hour are indistinguishable here. No rule reads this screen and no verdict moves because of it.
 *
 * ## ⚠ Two things from the mockup that are deliberately not here
 *
 * *"RECONCILE POLL 38s ago"* — there is no such timestamp anywhere. `via="poll"` exists, but a poll
 * that finds nothing new writes nothing and there is no heartbeat table, so production holds 1,159
 * `stream` rows and **zero** `poll`. The strip reports that instead, which answers the question the
 * reader actually has: the stream has never once lost a race to the safety net.
 *
 * *"Mute this name today"* — that is persistent per-operator state with no table behind it, and
 * muting a name is exactly the decision that should not be made by a button someone added while
 * building a layout. Left out until it is asked for.
 */
import { useMemo } from 'react';
import type { JSX } from 'react';
import { Link } from 'react-router-dom';

import { Num } from '../../design/components/data/Num.jsx';

export type NewsDeskCounts = {
  windowHours: number;
  stories: number;
  newestStoryAt?: string | null;
  newestSeenAt?: string | null;
  viaStream: number;
  viaPoll: number;
  viaUnknown: number;
  pricesFresh: number;
  pricesTotal: number;
  priceFreshMinutes: number;
  withheld: number;
  unmeasurable: number;
  filler: number;
  alerted: number;
  meetsGateNow: number;
  gateSigma: number;
  gatePct: number;
  typicalPerDay?: number | null;
  sigmaCeiling: number;
};

/** The subset of a news row this component needs. Kept structural so it cannot drift from the SDK. */
export type DeskItem = {
  articleId: string;
  headline: string;
  symbol?: string | null;
  name?: string | null;
  isin?: string | null;
  publishedAt: string;
  eventClass?: string | null;
  alerted?: boolean;
  moveSigma?: string | null;
  pctSinceNews?: string | null;
  baselinePrice?: string | null;
  lastPrice?: string | null;
  lastPriceAt?: string | null;
  priceSource?: string | null;
  priceStale?: boolean | null;
  lagSeconds?: number | null;
  impactScore?: number | null;
  expectedImpact?: number | null;
  siblingHeadlines?: number;
  url?: string | null;
  sessionPct?: string | null;
};

export function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 90) return `${Math.round(secs)}s`;
  if (secs < 5400) return `${Math.round(secs / 60)}m`;
  if (secs < 172_800) return `${Math.round(secs / 3600)}h`;
  return `${Math.round(secs / 86_400)}d`;
}

/**
 * How this row's significance was arrived at, in the reader's own terms.
 *
 * 🔴 **The point of the panel is that σ is checkable.** "4.5σ" means nothing on its own; "12.3%
 * against a company that normally moves 2.7% a day" is a claim someone can disagree with. Printing
 * the ratio without its denominator is how a derived number becomes folklore.
 */
export function measured(item: DeskItem): string[] {
  const lines: string[] = [];
  if (item.pctSinceNews != null && item.moveSigma != null) {
    const pct = Math.abs(Number(item.pctSinceNews));
    const sig = Math.abs(Number(item.moveSigma));
    // The denominator, recovered from the two numbers the API already sends, rather than asking for
    // a third: σ = move ÷ daily volatility, so the volatility is move ÷ σ.
    const vol = sig > 0 ? pct / sig : null;
    lines.push(
      vol == null
        ? `${pct.toFixed(1)}% move.`
        : `${pct.toFixed(1)}% against a name that normally moves ${vol.toFixed(1)}% a day.`,
    );
  } else if (item.moveSigma == null) {
    // ⚠ Not "0σ". A story with no stored volatility cannot be scored and cannot alert at all.
    lines.push('No stored volatility for this company, so significance is unmeasurable.');
  }
  if (item.baselinePrice && item.lastPrice) {
    lines.push(`${item.baselinePrice} → ${item.lastPrice}, read ${ago(item.lastPriceAt)} ago.`);
  }
  if (item.lagSeconds != null) {
    // How long after publication the baseline was captured. A long lag means the "since" window
    // already contained some of the move before this system saw it.
    lines.push(`Baseline captured ${item.lagSeconds}s after publication.`);
  }
  if (item.priceSource) lines.push(`Priced from ${item.priceSource}.`);
  return lines;
}

/** The freshness contract, stated. Every cell is a measured count. */
function Strip({ desk }: { desk: NewsDeskCounts }): JSX.Element {
  const carrying = desk.viaStream > 0;
  return (
    <div className="jmt-stk-nd__strip">
      <span className="jmt-stk-nd__cell">
        <span
          className={`jmt-stk-nd__dot${carrying ? '' : ' jmt-stk-nd__dot--off'}`}
          aria-hidden="true"
        />
        {carrying ? 'Stream carrying' : 'Stream silent'} · newest story{' '}
        {ago(desk.newestStoryAt)} old
      </span>
      {/* ⚠ Not "reconcile poll Ns ago" — see the module note. Zero is the healthy answer, so the
          cell says what zero means rather than showing a bare 0. */}
      <span className="jmt-stk-nd__cell">
        {desk.viaPoll === 0
          ? 'Stream won every race · poller claimed 0'
          : `Poller claimed ${desk.viaPoll} the stream missed`}
      </span>
      <span className="jmt-stk-nd__cell">
        Prices read ≤{desk.priceFreshMinutes}m for {desk.pricesFresh} of {desk.pricesTotal}
      </span>
      {/* ⛔ Withheld, not greyed. A dimmed 0.0% is still a number a reader can act on. */}
      {desk.withheld > 0 ? (
        <span className="jmt-stk-nd__cell jmt-stk-nd__cell--attn">
          <span aria-hidden="true">⚠</span> {desk.withheld} withheld · price older than the last
          session
        </span>
      ) : null}
      {desk.unmeasurable > 0 ? (
        <span className="jmt-stk-nd__cell jmt-stk-nd__cell--dim">
          {desk.unmeasurable} unmeasurable · no volatility history
        </span>
      ) : null}
    </div>
  );
}

/**
 * Three piles: what a message was sent for, what else arrived, and what was filler.
 *
 * ⛔ **Filler is counted and never listed.** `impact.EVENT_PATTERNS` matches listicles *first*, so
 * one that happens to mention "earnings" never reaches a real class; they score 0 by construction
 * and can never alert. Listing them would bury the rows that mean something under *"if you invested
 * $1,000 in Nvidia"*.
 *
 * ⚠ **Filler wins over `alerted`.** A filler row that carries an `alerted_at` was sent under an
 * older gate, before the class existed — and it belongs in the count, not at the top of the page.
 *
 * ⚠ The split reads `alerted`, which is a **record** of what went out. Recomputing "past the gate"
 * in the browser from the thresholds would let this page and the Telegram channel disagree about
 * what happened, and the channel is the one the operator acted on.
 */
export function split(items: DeskItem[]): {
  sent: DeskItem[];
  rest: DeskItem[];
  filler: number;
} {
  const sent: DeskItem[] = [];
  const rest: DeskItem[] = [];
  let filler = 0;
  for (const item of items) {
    if (item.eventClass === 'filler') filler += 1;
    else if (item.alerted) sent.push(item);
    else rest.push(item);
  }
  return { sent, rest, filler };
}

/**
 * The whole desk: strip, what was sent, then everything else.
 *
 * ⚠ **The split is on `alerted`, which is a record rather than a recomputation.** Deriving "past
 * the gate" in the browser from the thresholds would let the page and the Telegram channel disagree
 * about what happened, and the channel is the one the operator actually acted on.
 */
export function NewsDesk({
  desk,
  items,
}: {
  desk?: NewsDeskCounts | null;
  items: DeskItem[];
}): JSX.Element {
  const { sent, rest, filler } = useMemo(() => split(items), [items]);

  const gate = desk
    ? `${desk.gateSigma.toFixed(1)}σ and ${desk.gatePct.toFixed(1)}%`
    : null;

  return (
    <div className="jmt-stk-nd">
      {desk ? <Strip desk={desk} /> : null}

      <div className="jmt-stk-nd__band">
        {/* 🔴 "Sent", not "past the gate". The gate is stated beside it as the gate now in force,
            because the window can span a retune — and it does. */}
        <span className="jmt-stk-nd__bandlabel jmt-stk-nd__bandlabel--act">
          Sent to Telegram
        </span>
        <span className="jmt-stk-nd__rule" />
        {desk ? (
          <span
            className="jmt-stk-nd__bandnote"
            title={
              `The gate now requires both: a move of at least ${desk.gatePct.toFixed(1)}% AND at ` +
              `least ${desk.gateSigma.toFixed(1)} of this company's own daily sigmas. ` +
              `${desk.meetsGateNow} stories in this window meet it — which is not the same set as ` +
              `the ones a message was sent for, because one company's move is one message.`
            }
          >
            gate now {gate} · {desk.meetsGateNow} of {desk.stories} meet it
            {desk.typicalPerDay == null
              ? ' · rate unmeasured'
              : ` · ~${desk.typicalPerDay.toFixed(2)}/day typical`}
          </span>
        ) : null}
      </div>

      {sent.length === 0 ? (
        <p className="jmt-stk-nd__quiet">
          Nothing was sent in the last {desk?.windowHours ?? 48} hours.{' '}
          {desk?.typicalPerDay != null
            ? `At about ${desk.typicalPerDay.toFixed(2)} a day that is unremarkable — `
            : ''}
          silence here should match silence in the Telegram channel, and if it does not, the gate is
          not the thing that changed.
        </p>
      ) : (
        <div className="jmt-stk-nd__acts">
          {sent.map((item) => (
            <article className="jmt-stk-nd__act" key={item.articleId}>
              <div className="jmt-stk-nd__figure">
                {/* ⚠ **`Number()`, not the string.** `formatNum` returns a string value verbatim
                    and `Num` gates the sign, the colour AND the arrow on `typeof value ===
                    'number'`, so the API's `"12.30"` rendered a bare grey `12.30` with no percent
                    sign and no direction. That was live on this page until D82. */}
                {item.priceStale ? (
                  /* ⛔ Withheld rather than styled: the price predates the last completed session,
                     so the arithmetic would produce a confident percentage about nothing. */
                  <span className="jmt-stk-nd__withheld" title="Price older than the last session">
                    withheld
                  </span>
                ) : item.pctSinceNews == null ? (
                  <span className="jmt-stk-nd__withheld">—</span>
                ) : (
                  /* ⚠ `xl` (30px), not `display` (48px). Two reasons and both are real: the
                     ramp allows **one `display` per screen, ever** and the full feed below this
                     desk already spends it — and at 48px `−15.6%▼` is ~200px wide in a 180px
                     column, so it ran straight over the company name beside it. Grid columns do
                     not clip, so the overlap rendered silently. */
                  <Num value={Number(item.pctSinceNews)} kind="percent" dp={1} arrow step="xl" />
                )}
                <span className="jmt-stk-nd__sig">
                  {item.moveSigma == null
                    ? 'σ unmeasurable'
                    : `${Math.abs(Number(item.moveSigma)).toFixed(1)}σ`}
                  {item.eventClass ? ` · ${item.eventClass.replace(/_/g, ' ')}` : ''}
                </span>
                {item.baselinePrice && item.lastPrice ? (
                  <span className="jmt-stk-nd__prices">
                    {item.baselinePrice} → {item.lastPrice}
                  </span>
                ) : null}
              </div>

              <div className="jmt-stk-nd__body">
                <div className="jmt-stk-nd__who">
                  <span className="jmt-stk-nd__name">{item.name ?? item.symbol ?? '—'}</span>
                  {item.symbol ? <code className="jmt-stk-nd__sym">{item.symbol}</code> : null}
                  <span className="jmt-stk-nd__age">
                    {ago(item.publishedAt)} ago · price {ago(item.lastPriceAt)} ago
                  </span>
                </div>
                <p className="jmt-stk-nd__head">{item.headline}</p>
                <div className="jmt-stk-nd__actions">
                  {item.isin ? (
                    <Link className="jmt-stk-nd__btn" to={`/screener/${item.isin}`}>
                      Open {item.symbol ?? 'name'} in Screener
                    </Link>
                  ) : (
                    <span
                      className="jmt-stk-nd__btn jmt-stk-nd__btn--off"
                      title="This symbol has no universe row, so there is no dossier to open."
                    >
                      Not in the universe
                    </span>
                  )}
                  {item.url ? (
                    <a
                      className="jmt-stk-nd__btn"
                      href={item.url}
                      target="_blank"
                      rel="noreferrer noopener"
                    >
                      Source
                    </a>
                  ) : null}
                </div>
              </div>

              <div className="jmt-stk-nd__how">
                <span className="jmt-stk-nd__howlabel">How it was measured</span>
                {measured(item).map((line) => (
                  <span className="jmt-stk-nd__howline" key={line}>
                    {line}
                  </span>
                ))}
                {/* ⚠ One company's move is ONE message, so this alert stood for several
                    headlines. Without the count it reads as the only story about the event. */}
                {item.siblingHeadlines != null && item.siblingHeadlines > 0 ? (
                  <span className="jmt-stk-nd__dupes">
                    {item.siblingHeadlines} other headline
                    {item.siblingHeadlines === 1 ? '' : 's'} on this name today, collapsed into this
                    one
                  </span>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="jmt-stk-nd__band">
        <span className="jmt-stk-nd__bandlabel">Everything else · newest first</span>
        <span className="jmt-stk-nd__rule" />
        <span className="jmt-stk-nd__bandnote jmt-stk-nd__bandnote--dim">
          nothing here was sent
        </span>
      </div>

      <div className="jmt-stk-nd__rest" role="table">
        {rest.map((item) => {
          const sig = item.moveSigma == null ? null : Math.abs(Number(item.moveSigma));
          // ⛔ Capped at the ceiling, or one legitimate outlier flattens every other bar: RFAI
          // moved 270% on a formerly-flat SPAC and scores 540σ — right, and 35× the next row.
          const ceiling = desk?.sigmaCeiling ?? 4;
          const width = sig == null ? 0 : Math.min(100, (sig / ceiling) * 100);
          return (
            <div className="jmt-stk-nd__row" role="row" key={item.articleId}>
              <span className="jmt-stk-nd__cage" role="cell">
                {ago(item.publishedAt)}
              </span>
              <span className="jmt-stk-nd__csig" role="cell">
                <span className="jmt-stk-nd__signum">{sig == null ? '—' : sig.toFixed(1)}</span>
                <span className="jmt-stk-nd__bar" aria-hidden="true">
                  <span style={{ width: `${width}%` }} />
                </span>
              </span>
              <span className="jmt-stk-nd__cmove" role="cell">
                {item.priceStale ? (
                  <span className="jmt-stk-nd__withheld" title="Price older than the last session">
                    withheld
                  </span>
                ) : item.pctSinceNews == null ? (
                  <span className="jmt-stk-nd__withheld">—</span>
                ) : (
                  <Num value={Number(item.pctSinceNews)} kind="percent" dp={1} step="sm" />
                )}
              </span>
              <span className="jmt-stk-nd__cclass" role="cell">
                {(item.eventClass ?? 'other').replace(/_/g, ' ')}
              </span>
              <span className="jmt-stk-nd__cwho" role="cell">
                <span className="jmt-stk-nd__cname" title={item.name ?? undefined}>
                  {item.name ?? item.symbol ?? '—'}
                </span>
                {item.symbol ? <code className="jmt-stk-nd__sym">{item.symbol}</code> : null}
              </span>
              <span className="jmt-stk-nd__chead" role="cell" title={item.headline}>
                {item.headline}
              </span>
            </div>
          );
        })}
      </div>

      {filler > 0 ? (
        <p className="jmt-stk-nd__filler">
          {filler} filler {filler === 1 ? 'story' : 'stories'} hidden — listicles, “if you invested”
          pieces and index round-ups. They score zero by construction and never alert.
        </p>
      ) : null}
    </div>
  );
}
